import React from 'react';
import Localize from 'react-intl-universal';
import { useLocation } from 'react-router-dom';
import ScrollToTop from 'react-scroll-to-top';

import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { checkIsEventFree, checkIsEventIlias } from '@common/Util';
import BottomBox from '@components/BottomBox/BottomBox';
import CancelButton from '@components/BottomBox/CancelButton';
import Slider from '@components/Slider/Slider';
import * as routerConstants from '@navigation/Constants.js';

import AdditionalInformation from './AdditionalInformation/AdditionalInformation';
import BookingErrorDialog from './BookingErrorDialog/BookingErrorDialog';
import Checkout from './Checkout/Checkout';
import ComponentHeader from './ComponentHeader';
import ContactInformation from './ContactInformation/ContactInformation';
import Discounts from './Discounts/Discounts';
import useBookingProcess from './hooks/useBookingProcess';
import OrderSummary from './OrderSummary';
import BookingParticipants from './Participants/BookingParticipants';
import SessionsList from './SessionSelection/SessionsList';
import Summary from './Summary/Summary';

const StyledGrid = styled(Grid)(({ theme }) => ({
  marginTop: '0 !important',
  padding: '1rem 0 0 0',
  backgroundColor: [theme.palette?.background.primary],
  textAlign: 'left',
  '& ::-webkit-scrollbar': {
    width: '0.4em !important'
  }
}));

const StyledBooking = styled(Box)(({ theme }) => ({
  minHeight: 'calc(100vh - 5.725rem)',
  paddingBottom: '6rem',
  [theme.breakpoints.up('md')]: {
    paddingBottom: 0
  }
}));

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  height: '100%',
  padding: '2rem 1rem 1rem 1rem',
  overflow: 'auto',

  [theme.breakpoints.up('xs')]: { height: 'max-content' },
  [theme.breakpoints.up('md')]: { height: 'max-content' },
  [theme.breakpoints.up('lg')]: { height: '69.9vh' }
}));

const BookingProcess = ({ configuration }) => {
  const location = useLocation();
  const isLargeScreen = useMediaQuery('(min-width:1200px)');
  const theme = useTheme();

  const eventData = location?.state?.data || {};

  const {
    isLoading,
    sessions,
    handleSession,
    selectedSessions,
    totalObj,
    checkValue,
    contactsInfo,
    setContactsInfo,
    participants,
    setParticipants,
    bookingPersonRadio,
    setBookingPersonRadio,
    participantPriceAndDiscounts,
    availableSeats,
    isFormValid,
    setIsFormValid,
    activeStep,
    setActiveStep,
    steps,
    availableSteps,
    contextData,
    isLoggedIn,
    isCheckoutFormValid,
    setIsCheckoutFormValid,
    availableSeatsErrors,
    setWaitingListApproved,
    removeSession,
    discountOptions,
    checkedConsents,
    setCheckedConsents
  } = useBookingProcess(eventData, configuration);

  const isEventFree = checkIsEventFree(eventData);
  const isEventIlias = checkIsEventIlias(eventData);

  const disableKeyFields =
    availableSteps.length === 7 ? isLoggedIn && activeStep === 1 : isLoggedIn && activeStep === 0;

  return (
    <>
      <StyledBooking component='main'>
        <CancelButton
          redirectFrom={isLoggedIn ? 'AppNavBar.catalog' : 'AppNavBar.eventCatalog'}
          redirectPath={routerConstants.routes.name.catalog}
          goBack={location?.state?.goBack}
          sx={(theme) => ({
            display: { xs: 'block', md: 'none' },
            position: { xs: 'sticky', md: 'inherit' },
            zIndex: 1000,
            background: theme.palette.common.white,
            top: '2px',
            width: '95%',
            margin: 'auto'
          })}
        />
        <StyledGrid container spacing={2} sx={{ mt: '0px !important', pt: '0px !important' }}>
          <Grid item lg={2} xs={0} />

          <Grid
            container
            lg={8}
            xs={12}
            spacing={2}
            item
            sx={{ mt: '0px !important', pt: '0px !important' }}
          >
            {isLoading ? (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '84.7vh'
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Grid item lg={12} md={12} xs={12} sx={{ mt: 0, mb: '0px !important', pt: 0 }}>
                  <ComponentHeader
                    eventData={eventData}
                    availableSeats={availableSeats?.count || 0}
                  />
                </Grid>
                <Grid item lg={12} md={12} xs={12} sx={{ mt: '0.8rem', mb: '1rem', pt: 0 }}>
                  {steps && availableSteps.length && (
                    <Slider steps={steps} activeStep={activeStep} availableSteps={availableSteps} />
                  )}
                </Grid>
                <Grid item lg={8} xs={12}>
                  {{
                    0: (
                      <SessionsList
                        sessions={sessions}
                        handleSession={handleSession}
                        checkValue={checkValue}
                      />
                    ),
                    1: (
                      <ContactInformation
                        configuration={configuration}
                        contactsInfo={contactsInfo}
                        setContactsInfo={setContactsInfo}
                        setIsFormValid={setIsFormValid}
                        participants={participants}
                        setParticipants={setParticipants}
                        disableKeyFields={disableKeyFields}
                        setBookingPersonRadio={setBookingPersonRadio}
                        isEventFree={isEventFree}
                        isEventIlias={isEventIlias}
                      />
                    ),
                    2: (
                      <BookingParticipants
                        configuration={configuration}
                        bookingPersonRadio={bookingPersonRadio}
                        setIsFormValid={setIsFormValid}
                        participants={participants}
                        setParticipants={setParticipants}
                        maxParticipant={eventData?.maxParticipantNumberPerPublicBooking}
                        isEventFree={isEventFree}
                        isEventIlias={isEventIlias}
                      />
                    ),
                    3: (
                      <AdditionalInformation
                        setIsFormValid={setIsFormValid}
                        participants={participants}
                        setParticipants={setParticipants}
                        eventData={eventData}
                      />
                    ),
                    4: (
                      <Discounts
                        participants={participants}
                        setIsFormValid={setIsFormValid}
                        selectedSessions={selectedSessions}
                        setParticipants={setParticipants}
                        discountOptions={discountOptions}
                      />
                    ),
                    5: (
                      <Summary
                        participants={participants}
                        enableHotelRequest={eventData?.enableHotelRequest}
                      />
                    ),
                    6: (
                      <Checkout
                        participants={participants}
                        contactsInfo={contactsInfo}
                        setIsCheckoutFormValid={setIsCheckoutFormValid}
                        eventId={eventData?.id || null}
                        checkedConsents={checkedConsents}
                        setCheckedConsents={setCheckedConsents}
                        isEventFree={isEventFree}
                      />
                    )
                  }[availableSteps?.[activeStep] || 0] || (
                    <StyledBox>
                      <Container>
                        <Typography variant='h4' gutterBottom>
                          {Localize.get('Booking.noComponent')}
                        </Typography>
                      </Container>
                    </StyledBox>
                  )}
                </Grid>
                <BookingErrorDialog
                  eventData={eventData}
                  participants={participants}
                  availableSeats={availableSeats}
                  availableSeatsErrors={availableSeatsErrors}
                  setWaitingListApproved={setWaitingListApproved}
                  setParticipants={setParticipants}
                  goToDetails={location?.state?.goBack}
                  removeSession={removeSession}
                />
                <Grid item lg={4} xs={12}>
                  <OrderSummary
                    contextData={contextData}
                    selectedSessions={selectedSessions}
                    totalObj={totalObj}
                    activeStep={availableSteps?.[activeStep] || 0}
                    participants={participants}
                    bookingPersonRadio={bookingPersonRadio}
                    participantPriceAndDiscounts={participantPriceAndDiscounts}
                    steps={steps}
                    isCheckoutFormValid={isCheckoutFormValid}
                    eventData={eventData}
                    contactsInfo={contactsInfo}
                    availableSeats={availableSeats}
                    goToDetails={location?.state?.goBack}
                    checkedConsents={checkedConsents}
                    isEventFree={isEventFree}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Grid item lg={2} xs={0} />
          {!isLargeScreen && (
            <ScrollToTop
              smooth
              style={{ bottom: '11.5rem', color: theme.palette.booking.button }}
            />
          )}
        </StyledGrid>
        <BottomBox
          isLoading={isLoading}
          setActiveStep={setActiveStep}
          activeStep={activeStep}
          numberOfSteps={availableSteps?.length}
          forLink={location?.state || { data: {} }}
          isFormValid={isFormValid}
          redirectFrom={isLoggedIn ? 'AppNavBar.catalog' : 'AppNavBar.eventCatalog'}
          redirectPath={routerConstants.routes.name.catalog}
        />
      </StyledBooking>
    </>
  );
};
export default BookingProcess;
